/**
 * Persisted position of the floating transport panel (DEC-170).
 *
 * The panel docks just under the chrome bar's dot cluster by default, but a
 * grab handle lets the operator drag it anywhere on screen. Same idiom as
 * `clock/transportOffset.ts` (per-device, localStorage, never synced) - a
 * laptop preview and an iPad on stage can each leave it somewhere different.
 *
 * `null` = no drag has ever happened yet; the panel docks under the dot
 * cluster (computed from that button's own rect, not stored here).
 */

const KEY = 'opbuttons.transport.floater_pos_v1';

export interface FloaterPos {
  /** Viewport-relative, in CSS px - matches the `.shell` frame's own
   *  coordinate space (position: fixed inside a scaled shell tracks it 1:1,
   *  same reasoning DEC-096 modals rely on). */
  left: number;
  top: number;
}

export function loadFloaterPos(): FloaterPos | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed && typeof parsed === 'object' &&
      Number.isFinite((parsed as FloaterPos).left) &&
      Number.isFinite((parsed as FloaterPos).top)
    ) {
      return parsed as FloaterPos;
    }
  } catch {
    // fall through to null
  }
  return null;
}

export function saveFloaterPos(pos: FloaterPos): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(KEY, JSON.stringify(pos));
}

export function clearFloaterPos(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(KEY);
}
